import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Clock } from 'lucide-react';
import { toast } from 'sonner';
import { getApiCall } from '@/service/httpCall';
import { API_AGENT_BREAK_EXCEEDED_STATS } from '@/service/url';
import { DataTable } from '@/components/data-table/DataTable';
import { Input } from '@/components/ui/input';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const MAX_RANGE_MONTHS = 3;

export interface BreakRecord {
  id?: number | string;
  start_at?: string | null;
  end_at?: string | null;
  removed_at?: string | null;
  exceeded_seconds?: number | string | null;
  user?: { agent?: { id?: number | string; agent_name?: string } | null } | null;
  break?: { title?: string; duration?: number | string } | null;
}

export interface BreakStatsResponse {
  rows: BreakRecord[];
  total: number;
  exceeded: number;
}

export interface BreakRow {
  key: string | number;
  agent_id: string | number;
  agent_name: string;
  break_title: string;
  allowed_duration: number;
  started_at: DateParts;
  removed_at: DateParts;
  exceeded_time: string;
}

interface DateParts {
  date: string;
  time: string;
}

export function normalizeBreakStats(resp: unknown): BreakStatsResponse {
  if (Array.isArray(resp)) {
    const rows = resp as BreakRecord[];
    const exceeded = rows.filter((r) => Number(r.exceeded_seconds) > 0).length;
    return { rows, total: rows.length, exceeded };
  }
  const r = (resp || {}) as Partial<BreakStatsResponse>;
  return {
    rows: Array.isArray(r.rows) ? r.rows : [],
    total: Number(r.total) || 0,
    exceeded: Number(r.exceeded) || 0,
  };
}

export const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const minusMonths = (day: string, n: number) => {
  const [y = 0, m = 1, d = 1] = day.split('-').map(Number);
  const t = new Date(y, m - 1 - n, 1);
  const last = new Date(t.getFullYear(), t.getMonth() + 1, 0).getDate();
  t.setDate(Math.min(d, last));
  return ymd(t);
};

const toDateParts = (value?: string | null): DateParts => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return { date: '--', time: '' };
  return {
    date: date.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }),
    time: date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };
};

const formatDuration = (seconds: unknown) => {
  const s = Math.max(0, Number(seconds) || 0);
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

const getInitials = (name: string) => {
  const [a = '', b = ''] = name.trim().split(' ').filter(Boolean);
  if (!a) return 'NA';
  if (!b) return a.slice(0, 2).toUpperCase();
  return `${a[0]}${b[0]}`.toUpperCase();
};

export const toBreakRow = (r: BreakRecord, idx: number): BreakRow => ({
  key: r.id ?? idx + 1,
  agent_id: r.user?.agent?.id ?? '--',
  agent_name: r.user?.agent?.agent_name || '--',
  break_title: r.break?.title || '--',
  allowed_duration: Number(r.break?.duration) || 0,
  started_at: toDateParts(r.start_at),
  removed_at: toDateParts(r.end_at || r.removed_at),
  exceeded_time: formatDuration(r.exceeded_seconds),
});

function DateTimeCell({ parts }: { parts: DateParts }) {
  return (
    <div>
      <div className="font-semibold">{parts.date}</div>
      {parts.time && <div className="mt-0.5 text-xs text-muted-foreground">{parts.time}</div>}
    </div>
  );
}

export const breakRecordColumns: ColumnDef<BreakRow>[] = [
  { accessorKey: 'agent_id', header: 'Agent ID' },
  {
    accessorKey: 'agent_name',
    header: 'Agent Name',
    cell: ({ row }) => (
      <div className="flex items-center gap-2.5">
        <div
          className="flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold text-white"
          style={{ background: 'linear-gradient(135deg, #8b80f9 0%, #5f56da 100%)' }}
        >
          {getInitials(row.original.agent_name)}
        </div>
        <span>{row.original.agent_name}</span>
      </div>
    ),
  },
  { accessorKey: 'break_title', header: 'Break Title' },
  {
    accessorKey: 'allowed_duration',
    header: 'Allowed Duration',
    cell: ({ row }) => (
      <span className="rounded-full border px-2.5 py-1 text-xs font-semibold" style={{ color: '#1c8b55', borderColor: '#e6f2ff' }}>
        {row.original.allowed_duration} min
      </span>
    ),
  },
  {
    id: 'started_at',
    header: 'Started At',
    accessorFn: (r) => `${r.started_at.date} ${r.started_at.time}`.trim(),
    enableSorting: false,
    cell: ({ row }) => <DateTimeCell parts={row.original.started_at} />,
  },
  {
    id: 'removed_at',
    header: 'Removed At',
    accessorFn: (r) => `${r.removed_at.date} ${r.removed_at.time}`.trim(),
    enableSorting: false,
    cell: ({ row }) => <DateTimeCell parts={row.original.removed_at} />,
  },
  {
    accessorKey: 'exceeded_time',
    header: 'Exceeded Time',
    cell: ({ row }) => (
      <span
        className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
        style={{ color: '#d3304a', borderColor: '#e6f2ff' }}
      >
        <Clock className="h-3 w-3" />
        {row.original.exceeded_time}
      </span>
    ),
  },
];

export default function AgentBreakRecordsPage() {
  const today = todayIST();
  const minDate = minusMonths(today, MAX_RANGE_MONTHS);
  const defaultRange = { from: `${today.slice(0, 8)}01`, to: today };

  const [draft, setDraft] = useState(defaultRange);
  const [applied, setApplied] = useState(defaultRange);
  const [paging, setPaging] = useState({ pageIndex: 0, pageSize: 25 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState<BreakStatsResponse>({ rows: [], total: 0, exceeded: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === search) return;
      setSearch(next);
      setPaging((p) => ({ ...p, pageIndex: 0 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput, search]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = new URLSearchParams({
      start_date: applied.from,
      end_date: applied.to,
      page: String(paging.pageIndex),
      limit: String(paging.pageSize),
    });
    if (search) params.set('search', search);
    getApiCall(`${API_AGENT_BREAK_EXCEEDED_STATS}?${params.toString()}`, {})
      .then((resp: unknown) => { if (mounted) setData(normalizeBreakStats(resp)); })
      .catch(() => { if (mounted) setData({ rows: [], total: 0, exceeded: 0 }); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [applied, paging, search]);

  const rows = useMemo(() => data.rows.map(toBreakRow), [data.rows]);

  const apply = () => {
    if (!draft.from || !draft.to) return toast.error('Please select both start and end date.');
    if (draft.from > draft.to) return toast.error('Start date cannot be after end date.');
    if (draft.from < minDate || draft.to > today) {
      return toast.error(`Dates must be within the last ${MAX_RANGE_MONTHS} months (from ${minDate}).`);
    }
    setApplied({ ...draft });
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };

  const reset = () => {
    setDraft(defaultRange);
    setApplied(defaultRange);
    setSearchInput('');
    setSearch('');
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Agent Breaks Records"
        description={`Ended agent breaks · ${data.total} total · ${data.exceeded} exceeded`}
      />
      <DateRangeFilter
        className="mb-3"
        value={draft}
        min={minDate}
        max={today}
        onChange={setDraft}
        onApply={apply}
        onReset={reset}
      >
        <div className="min-w-[220px] flex-1">
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Search</label>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Agent ID, agent name or break title..."
            maxLength={100}
            className="h-10"
          />
        </div>
      </DateRangeFilter>
      <DataTable
        tableId="breaks.agent-break-records"
        columns={breakRecordColumns}
        data={rows}
        isLoading={loading}
        disableSearch
        emptyTitle="No break records"
        emptyDescription="No ended breaks in the selected range."
        pageSize={paging.pageSize}
        exportFilename="agent-break-records.csv"
        serverPagination={{ ...paging, rowCount: data.total, onChange: setPaging }}
      />
    </ScrollPage>
  );
}
